import {
  ApolloClient,
  InMemoryCache,
  Observable,
  from,
  split
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import { createClient } from 'graphql-ws'

// import { logout } from '../helpers/logout'
import { credentialsVar } from '../cache/auth'
import { ENV } from '../config/env'
import { redirectToLogin } from '../helpers/sessionRedirect'
import { lang } from '../localization'
import { isAuthenticationError, isLoginRoute } from './authError'
import { PAYMENT_FOLLOW_UP_TYPE_POLICY } from './paymentFollowUps'
import { mergeTicketPages } from './ticketCachePolicies'

// Exported for testing. Without it the suite can only exercise a hand-rolled
// stand-in that sets originPathname from a closure — so this link could stop
// setting it entirely and every test would still pass.
export const authLink = setContext((_, { headers }) => {
  return {
    // Where this request STARTED. The session-expiry link below needs it because
    // window.location can change between a request being sent and its response
    // arriving — logging in navigates away from /login while an earlier 401 is
    // still in flight.
    originPathname: window.location.pathname,
    // Internal request context only: never put principal identifiers in support
    // evidence or headers. The failure link compares this to the current session.
    supportPrincipal: credentialsVar()?.accessToken
      ? credentialsVar()?.email || ''
      : '',
    supportStartedAt: Date.now(),
    // Whether a session existed when this request was SENT. The pathname alone is
    // not enough to tell an expected pre-login 401 from a real expiry, because the
    // route gate is credential-based: after a successful login MainDrawer mounts
    // and issues its bootstrap queries while the URL is STILL /en/login, and only
    // then does PanelRoutes redirect to /home. Judging those by pathname would
    // discard a genuine expiry.
    hadCredentials: Boolean(credentialsVar()?.accessToken),
    headers: {
      'Accept-Language': lang,
      ...headers
    }
  }
})

const httpLink = createUploadLink({
  uri: ENV.GRAPH_BFF || 'http://localhost:8080',
  // uri: 'http://192.168.17.31:8080',
  credentials: 'include' // <--- important to send cookies
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: ENV.GRAPH_BFF_WS || 'ws://localhost:8080/'
  })
)

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  httpLink
)

export const createSessionExpiryLink = () =>
  onError(({ graphQLErrors, networkError, operation }) => {
    /*
     * Log what the gateway actually said on a network error, before deciding whether this
     * is an expiry.
     *
     * Apollo's ServerError.message is only "Response not successful: Received status code
     * N" — the response BODY is dropped from it. The gateway sends a body that names the
     * cause: `authUser_middleware.go` answers a rejected upload with either "invalid
     * GraphQL multipart request" or "invalid GraphQL operation", two different defects
     * behind the same 400. A QA report of an upload failing carried only the status, so
     * which of the two it was could not be established from the report at all.
     *
     * Console, deliberately not a toast. This codebase suppresses upstream message text on
     * the way to a user for a stated reason — a gateway cannot tell which upstream messages
     * echo caller input — and that reasoning is unchanged. A developer with devtools open
     * is the audience, and it is where the reporter was already looking when they captured
     * the Network tab.
     */
    if (networkError) {
      const server = networkError as {
        statusCode?: number
        result?: unknown
        bodyText?: string
      }
      if (server.statusCode && server.statusCode !== 401) {
        // eslint-disable-next-line no-console
        console.error(
          `[graphql] ${operation.operationName || 'operation'} → HTTP ${
            server.statusCode
          }`,
          server.result ?? server.bodyText ?? networkError.message
        )
      }
    }

    if (!isAuthenticationError(graphQLErrors, networkError)) return

    // Login and OTP pages need to receive their own 401 responses so they can
    // show an invalid-credentials message. Every authenticated page instead
    // enters the single session-expiry flow below.
    //
    // Judge that by where the request STARTED, not where the browser happens to
    // be when the response lands. Those differ across a successful login, and the
    // difference cost a whole login attempt every time:
    //
    //   1. page loads on /en/login; useBootstrapSession fires ME_QUERY
    //      (network-only) and gets a perfectly correct 401 — there is no session
    //   2. the user logs in; it succeeds, credentialsVar is set, the app renders
    //      MainDrawer and the URL becomes /en/home
    //   3. the 401 from step 1 NOW reaches this link. window.location.pathname is
    //      /en/home, so isLoginRoute() is false, the guard lets it through, and
    //      redirectToLogin() clears credentialsVar — tearing down the portal that
    //      had just mounted and returning the user to the login screen
    //
    // The second attempt then always worked, which made this look intermittent:
    // redirectToLogin latches an isRedirecting flag that is never reset, so it
    // can only do this once per page load.
    //
    // Reproduced on staging 2026-08-20 and confirmed by tracing localStorage
    // writes: exactly one 401, arriving after login, from a query (the stack goes
    // through fetchQueryByPolicy, not the login mutation).
    // A typeof guard, not an `as` cast. DefaultContext extends Record<string, any>,
    // so getContext().originPathname is statically `any` — an assertion there
    // fabricates a type over untyped data instead of narrowing a known one.
    //
    // That matters because isLoginRoute does RegExp.test(pathname), and
    // RegExp.prototype.test coerces via ToString. A non-string arriving here would
    // become "[object Object]" or "123", silently fail to match /login, and send a
    // request that DID start on the login page down the session-expiry path — this
    // bug again, from the opposite input.
    const context = operation.getContext()
    const contextOrigin = context.originPathname
    const originPathname =
      typeof contextOrigin === 'string' ? contextOrigin : undefined
    const hadCredentials = context.hadCredentials === true

    // Suppress ONLY a 401 that was expected: one sent from a login route with no
    // session in hand. Both conditions are required.
    //
    // Pathname alone is not sufficient, and getting that wrong inverts this bug
    // rather than fixing it. The route gate is credential-based
    // (credentials?.accessToken ? <MainDrawer /> : <AuthRoutes />), so after a
    // successful login MainDrawer mounts and fires its bootstrap queries — the
    // session-configuration query and pingSession — while window.location is STILL
    // /en/login; only then does PanelRoutes redirect to /home. A genuine expiry on
    // one of those would be silently discarded, leaving the user inside a portal
    // with a dead session and no path out.
    //
    // Credentials-at-send-time is the honest discriminator: no session means the
    // 401 is the expected pre-login one, a session means it is a real expiry
    // wherever it was sent from.
    if (
      !hadCredentials &&
      isLoginRoute(originPathname ?? window.location.pathname)
    )
      return

    redirectToLogin({ sessionExpired: true })

    // Screens frequently have independent query onError handlers. Completing
    // this request prevents each of those handlers from producing a duplicate
    // 401 toast while the browser navigates to the login screen.
    return new Observable((observer) => observer.complete())
  })

const logoutLink = createSessionExpiryLink()

const chainList = [logoutLink, authLink, splitLink]

// if (process.env.REACT_APP_ENVIRONMENT === 'production') {
//   const { sha256 } = require('crypto-hash')
//   const persistedLink = createPersistedQueryLink({
//     sha256,
//     useGETForHashedQueries: true
//   })
//   chainList.unshift(persistedLink)
// }

const linkChain = from(chainList)

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
    typePolicies: {
      PaymentFollowUp: PAYMENT_FOLLOW_UP_TYPE_POLICY,
      Query: {
        fields: {
          tickets: {
            // `search` MUST be a key arg. mergeTicketPages merges by id into
            // whichever cache entry the key args select, so leaving it out
            // would file two different searches under one entry and merge
            // their results — the previous term's rows would linger below the
            // new term's. Every argument that changes WHICH tickets come back
            // belongs here; only pageToken (which changes how many) does not.
            keyArgs: ['tab', 'search', 'pageSize'],
            merge: mergeTicketPages
          }
        }
      }
    }
  }),
  link: linkChain
})

export default apolloClient
